import './AIAssistantMount.scss';

import envConfig from '@hulu/env-config';
import {
  AIAssistant,
  AIAssistantProvider,
  createAssistantTransport,
  useAIAssistant,
  User,
} from 'mission-control-common-components';
import React, { useEffect, useMemo } from 'react';

import { sendTokenToWorker } from '../../configs/serviceWorkerInit';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { getCognitoDetailsCached } from '../../utils/cognitoDetails';
import AIAssistantErrorBoundary from './AIAssistantErrorBoundary';

const APP_NAME = 'mission-control-ui';
const PUSH_LAYOUT_CLASS = 'ai-assistant-open';

const getWsUrl = (): string =>
  (envConfig.REACT_APP_ASSISTANT_WS_URL || `${envConfig.REACT_APP_CAMPAIGN_API}graphql`).replace(/^http/, 'ws');

const getToken = async (): Promise<string> => {
  await User.checkIdToken(undefined, await getCognitoDetailsCached())
    .then(() => sendTokenToWorker(navigator))
    .catch((error) => console.error(error));

  return User.getUserInfo().idToken ?? '';
};

// KATANA-1543: reserve space on the #app shell for the open (fixed-position) drawer.
const PushLayout = (): null => {
  const { isOpen } = useAIAssistant();

  useEffect(() => {
    const app = document.getElementById('app');
    if (!app) return undefined;

    app.classList.toggle(PUSH_LAYOUT_CLASS, isOpen);

    return () => app.classList.remove(PUSH_LAYOUT_CLASS);
  }, [isOpen]);

  return null;
};

type AIAssistantMountProps = {
  children: React.ReactNode;
};

export const AIAssistantMount = ({ children }: AIAssistantMountProps): JSX.Element => {
  const { adsMcAiAssistantUiEnabled } = useFeatureFlags();

  const transport = useMemo(
    () => (adsMcAiAssistantUiEnabled ? createAssistantTransport({ url: getWsUrl(), getToken }) : undefined),
    [adsMcAiAssistantUiEnabled]
  );

  if (!adsMcAiAssistantUiEnabled) {
    return <>{children}</>;
  }

  return (
    <AIAssistantProvider config={{ appName: APP_NAME, enabled: true, transport }}>
      {children}
      <PushLayout />
      <AIAssistantErrorBoundary>
        <AIAssistant />
      </AIAssistantErrorBoundary>
    </AIAssistantProvider>
  );
};

export default AIAssistantMount;
